import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import type { Spacings } from '../../css/designTokens/spacing';

type StyledCenterProps = {
  $maxWidth?: string;
  $gutterWidth?: Spacings;
  $intrinsic?: boolean;
  children: ReactNode;
};

const StyledCenter = styled.div<StyledCenterProps>`
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  max-width: ${({ $maxWidth }) => $maxWidth};
  ${({ $gutterWidth }) => $gutterWidth && `padding: 0 var(--wui-${$gutterWidth});`}
  ${({ $intrinsic }) =>
    $intrinsic &&
    `
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`;

export type CenterProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * A CSS max-width value
   */
  maxWidth?: string;
  /**
   * The space on either side of the content
   */
  gutterWidth?: Spacings;
  /**
   * Center child elements based on their content width
   */
  intrinsic?: boolean;
  /**
   * The content to be centered
   */
  children: ReactNode;
};

/**
 * Center component provides various ways to center content horizontally within a container.
 * It supports max-width constraints, text alignment, gutters, and intrinsic content centering.
 */
export const Center = forwardRef<HTMLDivElement, CenterProps>(
  (
    { maxWidth = '100%', gutterWidth = 'space-00', intrinsic = false, children, ...props },
    ref,
  ): JSX.Element => (
    <StyledCenter
      ref={ref}
      $gutterWidth={gutterWidth}
      $intrinsic={intrinsic}
      $maxWidth={maxWidth}
      {...props}
    >
      {children}
    </StyledCenter>
  ),
);

Center.displayName = 'Center';
